export type ContactConfig = {
  errors: Record<string, string[]>
  isSaving: boolean
  isDirty: boolean
  contractLoaded: boolean
  emailIsLoading: boolean
  prefilledEmail: { status: boolean; message: string }
  siretIsLoading: boolean
  siretIsValid: boolean
  fiscalCodeIsLoading: boolean
  fiscalCodeIsValid: boolean | null
  newsletterData: Record<string, unknown>
  newsletterLoading: boolean
  newsletterError: string
  newsletterCanBeSent: boolean
  isValidZohoId: boolean
  isMcpClient: boolean
  isMcpLocked: boolean
  mcpUnlockError: string | null
  identityModal: boolean
  selectedSalesScriptSection: string | null
  emailSuggestion: string | null
}

export type ContactPersonal = {
  zohoId: string | null
  firstName: string | null
  firstName2: string | null
  lastName: string | null
  lastName2: string | null
  birthdate: string | null
  bornCity?: string | null
  civility: string | null
  civility2: string | null
  email: string | null
  phone1: string | null
  phone2: string | null
  secondName: boolean | null
  // Selectra-admin-only MCP advisor assignment (FR only). Source: backend
  // `mcp_advisory_id` on the contact. When set, an admin in FR can pick a
  // new advisor from the `mcp_users` list exposed in the page configuration.
  mcpAdvisoryId?: number | string | null
}

export type ContactPreferences = {
  clientType: string | null
  dataCollectionAuth: boolean
  neverBounceResult: string | boolean | null
  phone1Forced: boolean
  phone2Forced: boolean
  isNewsletterRegistered: boolean
  isReviewRegistered: boolean
  isClubSelectraRegistered: boolean
  createReviewsInteraction: string | null
  reviewLanguage: string | null
  whatsappConsent: boolean
  callbackDate: string | null
  callbackTime: string | null
  bacVoucher: boolean
  advisorCalendlyLink: string
  isOptInOutbound: boolean
}

export type ContactProfessional = {
  socialReason: string | null
  siret: string | null
  activity: string | null
  apeCode: string | null
  taxId?: string | null
}

export type ContactAdditional = {
  comments: string | null
  createdAt: string | null
  referral: boolean | null
  referralFirstName: string | null
  referralLastName: string | null
}

export type ContactIdentity = {
  documentType: string | null
  documentNumber: string | null
  expirationDate: string | null
  createdDate: string | null
  issuingEntity: string | null
  issuingCity: string | null
  taxIdCode: string | null
}

export type ContactSubscriptionData = {
  config: ContactConfig
  personal: ContactPersonal
  preferences: ContactPreferences
  professional: ContactProfessional
  additional: ContactAdditional
  identity: ContactIdentity
}

const contactSubscriptionData: ContactSubscriptionData = {
  config: {
    errors: {},
    isSaving: false,
    isDirty: false,
    contractLoaded: false,
    emailIsLoading: false,
    prefilledEmail: {
      status: false,
      message: '',
    },
    siretIsLoading: false,
    siretIsValid: false,
    fiscalCodeIsLoading: false,
    fiscalCodeIsValid: null,
    newsletterData: {},
    newsletterLoading: false,
    newsletterError: '',
    newsletterCanBeSent: true,
    isValidZohoId: false,
    isMcpClient: false,
    isMcpLocked: false,
    mcpUnlockError: null,
    identityModal: false,
    selectedSalesScriptSection: null,
    emailSuggestion: null,
  },
  personal: {
    zohoId: null,
    firstName: null,
    firstName2: null,
    lastName: null,
    lastName2: null,
    birthdate: null,
    civility: null,
    civility2: null,
    email: null,
    phone1: null,
    phone2: null,
    secondName: null,
    mcpAdvisoryId: null,
  },
  preferences: {
    clientType: null,
    dataCollectionAuth: false,
    neverBounceResult: null,
    phone1Forced: false,
    phone2Forced: false,
    isNewsletterRegistered: false,
    isReviewRegistered: false,
    isClubSelectraRegistered: false,
    createReviewsInteraction: null,
    reviewLanguage: null,
    whatsappConsent: false,
    callbackDate: null,
    callbackTime: null,
    bacVoucher: false,
    advisorCalendlyLink: '',
    isOptInOutbound: false,
  },
  professional: {
    socialReason: null,
    siret: null,
    activity: null,
    apeCode: null,
  },
  additional: {
    comments: null,
    createdAt: null,
    referral: null,
    referralFirstName: null,
    referralLastName: null,
  },
  identity: {
    documentType: null,
    documentNumber: null,
    expirationDate: null,
    createdDate: null,
    issuingEntity: null,
    issuingCity: null,
    taxIdCode: null,
  },
}

export default contactSubscriptionData
